<template>
  <div class="result-form">
    <div class="result-form__wrp">
      <div class="result-form__head">
        <div
          v-for="item in HEAD_OPTIONS"
          :key="item.position"
          :class="['result-form__head-el', item.type]"
        >
          <strong>{{ _T(item.title) }}</strong>
        </div>
      </div>
      <div class="result-form__items">
        <SelectedItem
          v-for="(item, index) in allItems"
          :key="index"
          :data-item="item"
          class="result-form__selected-item"
        />
      </div>
    </div>
    <div class="result-form__footer">
      <div class="result-form__footer-wrp">
        <ResultData class="result-form__result-item">
          <p class="result-form__title">{{ _T("@Total consumption") }}</p>
          <p class="result-form__result">
            <strong>{{ allItemsResult }}</strong> {{ _T("@Wh") }}
          </p>
          <p class="result-form__amount">
            {{ _T("@In total for") }} {{ allItems.length }}
            {{ getItemsTitle(allItems.length) }} за {{ maxTimeInHours }}
            {{ getTimeTitle(maxTimeInHours) }}
          </p>
        </ResultData>

        <ResultData class="result-form__result-item">
          <p class="result-form__title">{{ _T("@Working power") }}</p>
          <p class="result-form__result">
            <strong
              >~{{ isActiveCheckbox ? workPower : allItemsResult }}</strong
            >
            Вт
          </p>
          <p class="result-form__amount">
            {{ _T("@In total for") }} {{ allItems.length }}
            {{ getItemsTitle(allItems.length) }}
          </p>
        </ResultData>

        <ResultData
          v-if="isActiveCheckbox || hasStartupCoefficient"
          class="result-form__result-item"
        >
          <p class="result-form__title">{{ _T("@Starting power") }}</p>
          <p class="result-form__result">
            <strong>>{{ totalSelectedPower }}</strong> Вт
          </p>
          <p class="result-form__amount">
            {{ _T("@Simultaneous launch") }} {{ allItems.length }}
            {{ getItemsTitle(allItems.length) }}
          </p>
        </ResultData>
      </div>

      <StartingPowerDemand
        v-if="allItemsWithPowerDemand.length"
        :title="_T('@Devices requiring higher starting power')"
        :data-item="allItemsWithPowerDemand"
      />
    </div>

    <div class="result-form__checkbox">
      <BaseCheckbox v-model="isActiveCheckbox" :validate="{}">
        <span class="result-form__checkbox-label">{{
          _T("@Take into account the coefficient of simultaneous consumption")
        }}</span>
      </BaseCheckbox>
    </div>
  </div>
</template>

<script setup>
import SelectedItem from "~~/modules/calculators/powerCalculator/components/UI/SelectedItem.vue";
import ResultData from "~~/modules/calculators/powerCalculator/components/UI/ResultData.vue";
import StartingPowerDemand from "~~/modules/calculators/powerCalculator/components/UI/StartingPowerDemand.vue";
import { usePowerCalcStore } from "~/store/powerCalcStore";
import BaseCheckbox from "~/modules/shared/inputs/BaseCheckbox.vue";

const powerCalcStore = usePowerCalcStore();

const HEAD_OPTIONS = [
  { title: "@Devices", type: "items", position: 0 },
  { title: "@Power", type: "power", position: 1 },
  { title: "@Opening hours", type: "time", position: 2 },
];

const allItems = computed(() => {
  return powerCalcStore.getAllSelectedItems;
});

const allItemsResult = computed(() => {
  const totalPower = powerCalcStore.getAllSelectedItems.reduce((sum, item) => {
    let multiplier = 1;

    if (item.timeUnit === "minutes") {
      multiplier = item.timeValue / 60;
    } else if (item.timeUnit === "hours") {
      multiplier = item.timeValue;
    }

    const computedPower = Math.round(item.selectedPower * multiplier);

    return sum + computedPower;
  }, 0);

  return totalPower.toString();
});

const totalSelectedPower = computed(() => {
  return allItems.value.reduce((sum, item) => {
    const power = item.selectedPower || 0;
    const coefficient = item.startupCoefficient || 1;
    return sum + power * coefficient;
  }, 0);
});

const maxTimeInHours = computed(() => {
  const items = allItems.value.map((item) => {
    const timeInMinutes =
      item.timeUnit === "hours" ? item.timeValue * 60 : item.timeValue;
    return { ...item, timeInMinutes };
  });

  const maxTimeItem = items.reduce((maxItem, currentItem) => {
    return currentItem.timeInMinutes > maxItem.timeInMinutes
      ? currentItem
      : maxItem;
  });

  return Math.ceil(maxTimeItem.timeInMinutes / 60);
});

const workPower = computed(() => {
  const allItems = powerCalcStore.getAllSelectedItems;

  const totalPower = allItems.reduce((sum, item) => {
    return sum + (item.selectedPower || 0);
  }, 0);

  return Math.round(totalPower * 0.7);
});

const hasStartupCoefficient = computed(() => {
  return allItems.value.some((item) => !!item.startupCoefficient);
});

const allItemsWithPowerDemand = computed(() => {
  return powerCalcStore.getAllSelectedItems.filter(
    (item) => item.startingPowerDemand !== false,
  );
});

const props = defineProps({
  selectedItems: { type: Array, required: false },
});

const isActiveCheckbox = ref(false);

function getItemsTitle(length) {
  const ITEM_TITLE = "приладу";
  const ITEMS_TITLE = "приладів";

  return length === 1 ? ITEM_TITLE : ITEMS_TITLE;
}

function getTimeTitle(length) {
  const ITEM_TITLE = "годину";
  const ITEMS_TITLE = "години";

  return length === 1 ? ITEM_TITLE : ITEMS_TITLE;
}
</script>

<style lang="scss" scoped>
.result-form {
  @include flex-container(column);

  gap: 24px;

  @include bigMobile() {
    gap: 16px;
  }

  &__head {
    @include flex-container(row, flex-start);

    gap: 32px;
    padding: 16px;

    @include bigMobile() {
      display: none;
    }
  }

  &__head-el {
    &.items {
      max-width: 412px;
      width: 100%;
    }

    &.power {
      width: 138px;
      flex: 0 0 auto;
    }

    &.time {
      max-width: 304px;
      width: 100%;

      strong {
        width: 100%;

        display: inline-block;

        text-align: center;
      }
    }

    strong {
      @include font(20, 30, 600);
      color: var(--color-grey-900);
    }
  }

  &__selected-item {
    &:nth-child(odd) {
      background-color: var(--color-sky-lightest);
    }

    @include bigMobile() {
      background-color: var(--color-sky-lightest);
    }
  }

  &__items {
    @include bigMobile() {
      @include flex-container(column);

      gap: 8px;
    }
  }

  &__title {
    @include font(16, 22, 600);
    color: var(--color-sky-darker);

    @include bigMobile() {
      @include font(14, 18, 600);
    }
  }

  &__result {
    @include font(32, 44, 700);
    color: var(--color-grey-900);

    @include bigMobile() {
      @include font(24, 32, 700);
    }

    strong {
      color: var(--color-primary-base);
    }
  }

  &__amount {
    @include font(14, 18, 500);
    color: var(--color-sky-darker);
  }

  &__footer {
    @include flex-container(column);

    gap: 24px;
  }

  &__footer-wrp {
    @include flex-container(row, space-between);

    gap: 24px;

    @include bigMobile() {
      @include flex-container(column);

      gap: 8px;
    }
  }

  &__result-item {
    flex: 1;
  }

  &__checkbox {
    background-color: var(--color-sky-lightest);

    border-radius: 30px;

    padding: 16px;

    @include bigMobile() {
      border-radius: 16px;
    }
  }

  &__checkbox-label {
    @include font(16, 22, 600);
    color: var(--color-grey-900);
  }
}
</style>
